// src/hooks/useDialogBox.js

import { useCallback } from "react";

import { useDialogContext } from "../providers/DialogProvider";
import { getDialogError } from "../utils/sliceErrorUtils";

/**
 * Hook to trigger standardized modal messages (confirm, info, success, error).
 *
 * Supports confirmation callbacks, auto-closing dialogs, optional checkbox state,
 * shared error normalization, and stable helper references for effect-safe usage.
 *
 * @returns {{
 *   showDialog: Function,
 *   withConfirmation: Function,
 *   withConfirmAsync: Function,
 *   withConfirmAsyncEx: Function,
 *   withError: Function,
 * }} Stable dialog helpers backed by the shared DialogProvider.
 *
 * `showDialog(params, onConfirm?, onCancel?)`
 * Direct low-level entry point for info/success/confirm/error dialogs.
 * Supported `params` include:
 * - `type`: `"info" | "success" | "confirm" | "error"` (defaults to `"info"`)
 * - `message`: string or array of strings
 * - `callback`: overrides `onConfirm`
 * - `cancelCallback`: overrides `onCancel`
 * - `retryCallback`: renders a Retry button for error dialogs
 * - `autoClose` / `autoCloseDelay`
 * - `checkboxLabel` / `checkboxDefault`
 * - label overrides such as `confirmLabel`, `cancelLabel`, `okLabel`, `closeLabel`, `retryLabel`
 *
 * Button mapping:
 * - confirm dialogs: Confirm -> `callback` / `onConfirm`, Cancel -> `cancelCallback` / `onCancel`
 * - error dialogs: Close -> `cancelCallback` / `onCancel`, Retry -> `retryCallback`
 * - other dialogs: Okay -> `callback` / `onConfirm`
 *
 * @example
 * // Example: show a success dialog that auto-dismisses
 * const { showDialog } = useDialogBox();
 * showDialog({
 *   title: "Saved",
 *   message: "Your changes were saved successfully.",
 *   type: "success",
 *   autoClose: true
 * });
 *
 * @example
 * // Example: show a confirmation dialog with Yes/Cancel buttons
 * const { withConfirmation } = useDialogBox();
 * withConfirmation(
 *   {
 *     title: "Discard changes?",
 *     message: "You have unsaved changes. Continue without saving?",
 *     confirmLabel: "Yes, discard",
 *     cancelLabel: "No, keep editing"
 *   },
 *   () => {
 *     // callback: runs if user confirms
 *     discardChanges();
 *   },
 *   () => {
 *     // cancelCallback: runs if user cancels
 *     console.log("User chose to stay");
 *   }
 * );
 *
 * @example
 * // Example: show a normalized error dialog with a custom Close path
 * const { withError } = useDialogBox();
 * withError(apiError, () => retryLoad(), {
 *   fallbackMessage: "Retrieve Baseline Error",
 *   onClose: () => navigate("/baseline", { replace: true }),
 * });
 */
export const useDialogBox = () => {
	const { setMsgBox } = useDialogContext();

	// Keep dialog helpers referentially stable so page-level effects that depend
	// on them do not re-fire the same dialog on every parent re-render.
	const showDialog = useCallback((dialogParams, onConfirm = null, onCancelCallback = null) => {
		const {
			type = "info",
			autoClose = false,
			autoCloseDelay = 2500,
			retryCallback,
			callback,
			cancelCallback,
			checkboxLabel,
			checkboxDefault = false,
			...rest
		} = dialogParams;

		let timer;

		const dialog = {
			type,
			checkboxLabel,
			checkboxDefault,
			...rest,
			callback: (val) => {
				if (autoClose) clearTimeout(timer);
				if (typeof callback === "function") {
					callback(val);
				} else if (typeof onConfirm === "function") {
					onConfirm(val);
				}
				setMsgBox(null);
			},
			cancelCallback: (val) => {
				if (autoClose) clearTimeout(timer);
				if (typeof cancelCallback === "function") {
					cancelCallback(val);
				} else if (typeof onCancelCallback === "function") {
					onCancelCallback(val);
				}
				setMsgBox(null);
			},
			...(retryCallback
				? {
						retryCallback: () => {
							if (autoClose) clearTimeout(timer);
							if (typeof retryCallback === "function") {
								retryCallback();
							}
							setMsgBox(null);
						},
				  }
				: {}),
		};

		if (autoClose) {
			timer = setTimeout(() => {
				setMsgBox(null);
			}, autoCloseDelay);
		}

		setMsgBox(dialog);
	}, [setMsgBox]);

	const withConfirmation = useCallback((params, onConfirm, onCancel = null) => {
		showDialog({ ...params, type: "confirm" }, onConfirm, onCancel);
	}, [showDialog]);

	// Accept either a plain string or a preserved API error payload so callers
	// can forward the shared slice error state directly when appropriate.
	const withError = useCallback((message, onRetry = null, options = {}) => {
		const resolvedError = getDialogError(message, options.fallbackMessage, options);
		showDialog({
			title: options.title || "Error",
			message: resolvedError.message,
			type: "error",
			cancelCallback: options.onClose || null,
			closeLabel: options.closeLabel,
			retryCallback: resolvedError.retryable ? onRetry : null,
		});
	}, [showDialog]);

	const withConfirmAsync = useCallback((opts) =>
		new Promise((resolve) => {
			setTimeout(() => {
				withConfirmation(
					opts,
					() => resolve(true),
					() => resolve(false)
				);
			}, 0);
		}), [withConfirmation]);

	const withConfirmAsyncEx = useCallback((opts) =>
		new Promise((resolve) => {
			setTimeout(() => {
				withConfirmation(
					opts,
					(val) => resolve({ accepted: true, value: val }),
					(val) => resolve({ accepted: false, value: val })
				);
			}, 0);
		}), [withConfirmation]);

	return { showDialog, withConfirmation, withConfirmAsync, withConfirmAsyncEx, withError };
};
